/**
 * VanHoaPhanHeHandler — đồng bộ PH + gate quyền Văn hóa.
 *
 * ProfilePhanHeSync refetch profile theo phân hệ vì API legacy chưa trả contract đa phân hệ.
 *
 * RecoilPhanHeSync đồng bộ Recoil + auth + storage.
 * VanHoaAppPageRenderer: filterListRoleByPhanHe(vanhoa) + isAdminVanHoa.
 *
 * Export: normalizeRoutePath, resolveEffectivePathname, hasVanHoaModuleRead,
 * shouldRunProfilePhanHeSync, VanHoaPhanHeSync, VanHoaRoutePrefetch, VanHoaAppPageRenderer.
 */
import type { AppProps } from 'next/app';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import { useRouter } from 'next/router';
import { useSetRecoilState } from 'recoil';
import { phanHe } from '@/states/authen';
import {
  getPhanHeFromStorage,
  resolvePhanHeFromRoute,
  resolveProfilePhanHe,
  setPhanHeToStorage,
  subscribePhanHeStorage,
} from '@/utils/phanHeStorage';
import { filterListRoleByPhanHe } from '@/utils/phanHeRoles';
import { useAuthStore } from '@/store/useAuthStore';
import { authApi, parseUserProfileResponse } from '@/services/authen';
import { getModulePermission } from '@/hooks/usePermission';
import { getControllerFromRoute } from '@/helper/RouteHelper';
import NoPermissionCard from '@/components/common/NoPermissionCard';
import type { RolePermission } from '@/providers/AuthenProvider';
import { CONSTANTS } from '@/utils/constants';
import { isStandaloneDotOnTapDetailPath, normalizeHeaderPath } from '@/configs/ROUTE_HEADER';
import { isVanHoaPath } from '@/utils/vanHoaHuongDan';
import {
  prefetchVanHoaRouteNow,
  scheduleVanHoaRoutePrefetch,
  warmVanHoaOrgCache,
} from '@/utils/vanHoaRoutePrefetch';
import { hasVanHoaRouteRead } from '@/utils/vanHoaPermission';

const VH_THIETLAP_CONTROLLERS = new Set(['bophan', 'chucvu', 'nhanvien', 'donvi', 'nhomquyen']);

export const normalizeRoutePath = (value?: string | null) =>
  String(value || '').split('?')[0].split('#')[0].replace(/\/$/, '') || '/';

/** Next.js client transition tạm set pathname `/404` — lấy route thực từ asPath. */
export const resolveEffectivePathname = (pathname: string, asPath: string) => {
  const normalizedPathname = normalizeRoutePath(pathname);
  const normalizedAsPath = normalizeRoutePath(asPath);
  if (normalizedPathname === '/404' && normalizedAsPath !== '/404') {
    return normalizedAsPath;
  }
  return normalizedPathname;
};

const isTransient404Route = (pathname: string, asPath: string) =>
  normalizeRoutePath(pathname) === '/404' && normalizeRoutePath(asPath) !== '/404';

export const hasVanHoaModuleRead = (listRole: RolePermission[] | undefined | null): boolean => {
  if (!Array.isArray(listRole)) return false;
  return listRole.some((r) => {
    if (!r?.read) return false;
    const ctrl = String(r.controller || '');
    if (ctrl.startsWith('vanhoa') || ctrl === 'daotaovanhoa') return true;
    if (VH_THIETLAP_CONTROLLERS.has(ctrl)) return true;
    return ctrl.startsWith('thietlap.') || ctrl.startsWith('thietlap_');
  });
};

/** Chỉ sync profile/role khi VH, intro, QT hoặc BSC nhưng storage còn phân hệ khác. */
export function shouldRunProfilePhanHeSync(pathname: string, asPath: string): boolean {
  const routeCandidates = [normalizeHeaderPath(asPath), normalizeHeaderPath(pathname)];
  if (routeCandidates.some((path) => isStandaloneDotOnTapDetailPath(path))) {
    return false;
  }

  const routePhanHe = resolvePhanHeFromRoute(pathname, asPath);
  if (routePhanHe === CONSTANTS.PHAN_HE.VAN_HOA) return true;
  if (routePhanHe === CONSTANTS.PHAN_HE.QUY_TRINH) return true;
  if (normalizeRoutePath(asPath || pathname) === '/intro') return true;
  if (routePhanHe === CONSTANTS.PHAN_HE.BSC) {
    return true;
  }
  return false;
}

/** Đồng bộ quyền profile theo phân hệ đang mở. */
function ProfilePhanHeSync({
  pathname,
  asPath,
  onSyncingChange,
}: {
  pathname: string;
  asPath: string;
  onSyncingChange?: (syncing: boolean) => void;
}) {
  const profileId = useAuthStore((state) => state.profile?.id);
  const isAuth = useAuthStore((state) => state.isAuth);
  const setPhanHe = useAuthStore((state) => state.setPhanHe);
  const setProfile = useAuthStore((state) => state.setProfile);
  const lastSyncedPhanHeRef = useRef<string | null>(null);
  const loadingRef = useRef(false);
  const onSyncingChangeRef = useRef(onSyncingChange);
  onSyncingChangeRef.current = onSyncingChange;

  const normalizedProfileId = useMemo(
    () => String(profileId || '').replace(/_nv$/, ''),
    [profileId],
  );

  const shouldSync = useMemo(
    () => shouldRunProfilePhanHeSync(pathname, asPath),
    [pathname, asPath],
  );

  useEffect(() => {
    if (!shouldSync) {
      onSyncingChangeRef.current?.(false);
    }
  }, [shouldSync]);

  useEffect(() => {
    lastSyncedPhanHeRef.current = null;
  }, [normalizedProfileId]);

  const loadProfileForPhanHe = useCallback(async (phanHeValue: string) => {
    if (!profileId) {
      onSyncingChangeRef.current?.(false);
      return;
    }
    if (lastSyncedPhanHeRef.current === phanHeValue) {
      onSyncingChangeRef.current?.(false);
      return;
    }
    if (loadingRef.current) {
      onSyncingChangeRef.current?.(true);
      return;
    }

    loadingRef.current = true;
    onSyncingChangeRef.current?.(true);
    setPhanHe(phanHeValue);
    try {
      const responseProfile = await authApi.requestUserProfile(profileId, phanHeValue);
      const nextProfile = parseUserProfileResponse(responseProfile);
      if (nextProfile) {
        setProfile(nextProfile);
        lastSyncedPhanHeRef.current = phanHeValue;
      }
    } catch {
      // Cho phép retry khi đổi phân hệ/route nếu API profile tạm lỗi.
    } finally {
      loadingRef.current = false;
      onSyncingChangeRef.current?.(false);
    }
  }, [profileId, setPhanHe, setProfile]);

  const resolvedPhanHe = useMemo(() => {
    const routePhanHe = resolvePhanHeFromRoute(pathname, asPath);
    if (routePhanHe === CONSTANTS.PHAN_HE.VAN_HOA) return routePhanHe;
    return resolveProfilePhanHe(pathname, asPath);
  }, [pathname, asPath]);

  useLayoutEffect(() => {
    if (!isAuth || !profileId || !shouldSync) return;
    void loadProfileForPhanHe(resolvedPhanHe);
  }, [resolvedPhanHe, isAuth, profileId, shouldSync, loadProfileForPhanHe]);

  useEffect(() => {
    if (!isAuth || !profileId || !shouldSync) return;
    return subscribePhanHeStorage((value) => {
      if (!value) return;
      lastSyncedPhanHeRef.current = null;
      void loadProfileForPhanHe(value);
    });
  }, [isAuth, profileId, shouldSync, loadProfileForPhanHe]);

  return null;
}

function RecoilPhanHeSync({ pathname, asPath }: { pathname: string; asPath: string }) {
  const setPhanHeRecoil = useSetRecoilState(phanHe);
  const setPhanHeAuth = useAuthStore((state) => state.setPhanHe);
  const lastRoutePhanHeRef = useRef<string | null>(null);

  useLayoutEffect(() => {
    const routePhanHe = resolvePhanHeFromRoute(pathname, asPath);
    if (routePhanHe) {
      if (lastRoutePhanHeRef.current !== routePhanHe) {
        lastRoutePhanHeRef.current = routePhanHe;
        setPhanHeRecoil(routePhanHe);
        setPhanHeAuth(routePhanHe);
        if (getPhanHeFromStorage() !== routePhanHe) {
          setPhanHeToStorage(routePhanHe);
        }
      }
      return;
    }

    const saved = getPhanHeFromStorage();
    if (saved && lastRoutePhanHeRef.current !== saved) {
      lastRoutePhanHeRef.current = saved;
      setPhanHeRecoil(saved);
      setPhanHeAuth(saved);
    }
  }, [pathname, asPath, setPhanHeRecoil, setPhanHeAuth]);

  useEffect(() => {
    const unsubscribe = subscribePhanHeStorage((value) => {
      const routePhanHe = resolvePhanHeFromRoute(pathname, asPath);
      if (routePhanHe) {
        if (lastRoutePhanHeRef.current !== routePhanHe) {
          lastRoutePhanHeRef.current = routePhanHe;
          setPhanHeRecoil(routePhanHe);
          setPhanHeAuth(routePhanHe);
        }
        return;
      }

      if (value && lastRoutePhanHeRef.current !== value) {
        lastRoutePhanHeRef.current = value;
        setPhanHeRecoil(value);
        setPhanHeAuth(value);
      }
    });

    return () => {
      unsubscribe?.();
    };
  }, [pathname, asPath, setPhanHeRecoil, setPhanHeAuth]);

  return null;
}

type VanHoaPhanHeSyncProps = {
  pathname: string;
  asPath: string;
  onProfileSyncingChange?: (syncing: boolean) => void;
};

/** Prefetch chunk + warm org cache khi vào / chuyển trong phân hệ VH. */
export function VanHoaRoutePrefetch() {
  const router = useRouter();

  useEffect(() => {
    if (!router.isReady || !isVanHoaPath(router.pathname)) return;
    warmVanHoaOrgCache();
    scheduleVanHoaRoutePrefetch(router);
  }, [router, router.isReady, router.pathname]);

  useEffect(() => {
    if (!router.isReady) return;

    const onRouteStart = (url: string) => {
      if (!isVanHoaPath(router.asPath) || !isVanHoaPath(url)) return;
      prefetchVanHoaRouteNow(router, url);
    };

    router.events.on('routeChangeStart', onRouteStart);
    return () => router.events.off('routeChangeStart', onRouteStart);
  }, [router, router.isReady, router.events, router.asPath]);

  useEffect(() => {
    if (!router.isReady || !isVanHoaPath(router.pathname)) return;

    const onHover = (event: MouseEvent) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      const anchor = target.closest('a[href^="/van-hoa"]') as HTMLAnchorElement | null;
      const href = anchor?.getAttribute('href');
      if (!href) return;
      prefetchVanHoaRouteNow(router, href);
    };

    document.addEventListener('mouseover', onHover, { passive: true });
    return () => document.removeEventListener('mouseover', onHover);
  }, [router, router.isReady, router.pathname]);

  return null;
}

/** Đồng bộ phân hệ (Recoil + reload profile/role khi đổi Văn hóa). */
export function VanHoaPhanHeSync({ pathname, asPath, onProfileSyncingChange }: VanHoaPhanHeSyncProps) {
  const shouldSync = shouldRunProfilePhanHeSync(pathname, asPath);

  return (
    <>
      <VanHoaRoutePrefetch />
      <RecoilPhanHeSync pathname={pathname} asPath={asPath} />
      {shouldSync && (
        <ProfilePhanHeSync pathname={pathname} asPath={asPath} onSyncingChange={onProfileSyncingChange} />
      )}
    </>
  );
}

type VanHoaAppPageRendererProps = {
  pathname: string;
  asPath: string;
  isSurveyTakingPage: boolean;
  profilePhanHeSyncing: boolean;
  Component: AppProps['Component'];
  pageProps: AppProps['pageProps'];
};

/** Kiểm tra quyền route Văn hóa + giữ page khi Next.js transient `/404`. */
function VanHoaAppPageRendererInner({
  pathname,
  asPath,
  profilePhanHeSyncing,
  Component,
  pageProps,
}: Omit<VanHoaAppPageRendererProps, 'isSurveyTakingPage'>) {
  const profileUser = useAuthStore((state) => state.profile);
  const moduleReadRef = useRef(false);

  useEffect(() => {
    moduleReadRef.current = false;
  }, [profileUser?.id]);

  const lastRenderedPageRef = useRef<{
    Component: AppProps['Component'];
    pageProps: AppProps['pageProps'];
  } | null>(null);

  const effectivePathname = useMemo(
    () => resolveEffectivePathname(pathname, asPath),
    [pathname, asPath],
  );
  const isTransient404 = isTransient404Route(pathname, asPath);

  const routeController = getControllerFromRoute(effectivePathname);

  const hasVanHoaAccess = useMemo(() => {
    if (profileUser?.isAdminVanHoa || profileUser?.isAdmin) return true;
    if (moduleReadRef.current) return true;
    const vhRoles = filterListRoleByPhanHe(
      profileUser?.listRole as RolePermission[] | undefined,
      CONSTANTS.PHAN_HE.VAN_HOA,
    );
    if (hasVanHoaModuleRead(vhRoles as RolePermission[] | undefined)) {
      moduleReadRef.current = true;
      return true;
    }
    const routePermission = getModulePermission(
      profileUser,
      routeController,
      undefined,
      CONSTANTS.PHAN_HE.VAN_HOA,
    );
    if (routePermission?.read) return true;
    if (routeController && hasVanHoaRouteRead(vhRoles, routeController)) return true;
    return false;
  }, [profileUser, routeController]);

  const showVanHoaNoPermission = !profilePhanHeSyncing && !hasVanHoaAccess;

  if (showVanHoaNoPermission) {
    return <NoPermissionCard textDetail="Bạn không có quyền truy cập vào trang này." />;
  }

  if (!isTransient404) {
    lastRenderedPageRef.current = { Component, pageProps };
    return <Component {...pageProps} />;
  }

  if (lastRenderedPageRef.current) {
    const { Component: PrevPage, pageProps: prevPageProps } = lastRenderedPageRef.current;
    return <PrevPage {...prevPageProps} />;
  }

  return <Component {...pageProps} />;
}

export function VanHoaAppPageRenderer({
  pathname,
  asPath,
  isSurveyTakingPage,
  profilePhanHeSyncing,
  Component,
  pageProps,
}: VanHoaAppPageRendererProps) {
  const isVanHoaRoute =
    resolvePhanHeFromRoute(pathname, asPath) === CONSTANTS.PHAN_HE.VAN_HOA && !isSurveyTakingPage;

  if (!isVanHoaRoute) {
    return <Component {...pageProps} />;
  }

  return (
    <VanHoaAppPageRendererInner
      pathname={pathname}
      asPath={asPath}
      profilePhanHeSyncing={profilePhanHeSyncing}
      Component={Component}
      pageProps={pageProps}
    />
  );
}
